import { useState, useEffect } from "react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LogOut,
  LayoutDashboard,
  Calendar,
  BookOpen,
  Image,
  Users,
  MessageSquare,
  Scissors,
  Plus,
  Edit,
  Trash2,
  Check,
  X,
  RefreshCw,
  Eye,
  CalendarDays,
  Clock,
  Receipt,
  Mail,
  Phone,
  ChevronLeft,
  ChevronRight,
  Send,
  Upload,
  Copy,
  Search,
  ImagePlus
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = () => axios.create({ withCredentials: true });
// removed consultation-specific constant

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: <LayoutDashboard size={16} /> },
  { id: "bookings", label: "Wizyty", icon: <Calendar size={16} /> },
  { id: "calendar", label: "Kalendarz", icon: <CalendarDays size={16} /> },
  { id: "trainings", label: "Szkolenia", icon: <BookOpen size={16} /> },
  { id: "uslugi", label: "Usługi / Cennik", icon: <Receipt size={16} /> },
  { id: "gallery", label: "Galeria", icon: <Image size={16} /> },
  { id: "media", label: "Biblioteka Zdjęć", icon: <ImagePlus size={16} /> },
  { id: "partners", label: "Partnerzy", icon: <Users size={16} /> },
  { id: "contacts", label: "Wiadomości", icon: <MessageSquare size={16} /> },
  { id: "emails", label: "Szablony E-mail", icon: <Mail size={16} /> },
];

const STATUS_COLORS = {
  pending: "text-yellow-400 border-yellow-400/30",
  confirmed: "text-blue-400 border-blue-400/30",
  completed: "text-green-400 border-green-400/30",
  cancelled: "text-red-400 border-red-400/30",
  paid: "text-green-400 border-green-400/30",
};
const STATUS_PL = {
  pending: "Oczekuje", confirmed: "Potwierdzona", completed: "Ukończona",
  cancelled: "Anulowana", paid: "Opłacona",
};

function StatCard({ label, value, sub, color = "violet" }) {
  return (
    <div className="border border-white/8 p-6 card-hover" style={{ background: "#0A0A0A" }}>
      <div className={`font-anton text-4xl mb-1 ${color === "violet" ? "gradient-text" : "text-green-400"}`}>{value}</div>
      <div className="text-white text-sm font-outfit">{label}</div>
      {sub && <div className="text-white/30 text-xs mt-1">{sub}</div>}
    </div>
  );
}

function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const LIMIT = 200;
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({});
  const [search, setSearch] = useState("");

  const load = (
    newSkip = 0,
    append = false,
    query = search
  ) => {
    const params = new URLSearchParams({
      skip: String(newSkip),
      limit: String(LIMIT)
    });

    if (query.trim()) {
      params.set("search", query.trim());
    }

    ax()
      .get(`${API}/bookings?${params.toString()}`)
      .then(r => {
        const data = r.data;
        const items = data.items || data;

        setTotal(
          typeof data.total === "number"
            ? data.total
            : items.length
        );

        setBookings(prev =>
          append
            ? [...prev, ...items]
            : items
        );

        setLoading(false);
      })
      .catch(error => {
        console.error(error);
        setLoading(false);
      });
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      setSkip(0);
      load(0, false, search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const updateStatus = async (id, status) => {
    await ax().put(`${API}/bookings/${id}`, { status });
    load(0, false, search);
    setSkip(0);
  };
  const del = async id => {
    if (!window.confirm("Usunąć tę rezerwację?")) return;

    await ax().delete(`${API}/bookings/${id}`);
    load(0, false, search);
    setSkip(0);
  };
  const startEdit = (b) => setEditing(prev => ({ ...prev, [b.id]: { row_label: b.row_label || "", row_price: b.row_price || "" } }));
  const saveEdit = async id => {
    await ax().put(`${API}/bookings/${id}`, editing[id]);

    setEditing(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

    load(0, false, search);
  };

  if (loading) return <div className="text-white/40 py-8">Ładowanie...</div>;
  const pending = bookings.filter(b => b.status === "pending");
  return (
    <div data-testid="bookings-table">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-anton text-2xl text-white uppercase">Wizyty ({search.trim() ? total : bookings.length})</h2>
        <button
          onClick={() => load(0, false, search)}
          className="text-white/40 hover:text-white"
        >
          <RefreshCw size={16} />
        </button>
      </div>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="relative w-full max-w-md">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30"
        />

        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Szukaj po imieniu i nazwisku..."
          className="w-full bg-[#0A0A0A] border border-white/10 text-white text-sm py-3 pl-10 pr-10 outline-none focus:border-violet-600 transition-colors"
          data-testid="booking-search"
        />

        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white"
            title="Wyczyść wyszukiwanie"
          >
            <X size={15} />
          </button>
        )}
      </div>

      {search.trim() && (
        <div className="text-white/30 text-xs">
          Znaleziono: {total}
        </div>
      )}
    </div>

      {/* Pending bookings alert */}
      {pending.length > 0 && (
        <div className="mb-6 border border-yellow-500/30 p-4" style={{ background: "rgba(234,179,8,0.05)" }}>
          <div className="text-yellow-400 text-xs uppercase tracking-[0.15em] mb-3 font-outfit font-medium">
            {pending.length} rezerwacja/e oczekuje na potwierdzenie
          </div>
          <div className="space-y-2">
            {pending.map(b => (
              <div key={b.id} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
                <div>
                  <span className="text-white text-sm">{b.client_name}</span>
                  <span className="text-white/40 text-xs ml-2">{b.client_email}</span>
                  <span className="text-white/30 text-xs ml-2">· {b.date} o {b.time}</span>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`confirm-booking-${b.id}`}
                    onClick={() => updateStatus(b.id, "confirmed")}
                    className="text-xs px-3 py-1.5 bg-green-500/15 border border-green-500/30 text-green-400 hover:bg-green-500/25 transition-colors uppercase tracking-[0.1em]">
                    Potwierdź
                  </button>
                  <button data-testid={`reject-booking-${b.id}`}
                    onClick={() => updateStatus(b.id, "cancelled")}
                    className="text-xs px-3 py-1.5 bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors uppercase tracking-[0.1em]">
                    Odrzuć
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/5">
              {["Klientka", "Usługa / Stopień", "Cena", "Data & Godzina", "Uwagi", "Status", "Akcje"].map(h => (
                <th key={h} className="text-left py-3 px-3 text-white/30 text-xs uppercase tracking-[0.12em] font-outfit">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bookings.map(b => (
              <tr key={b.id} className={`border-b border-white/5 hover:bg-white/2 transition-colors ${b.status === "pending" ? "bg-yellow-500/3" : ""}`}>
                {/* Klientka */}
                <td className="py-4 px-3">
                  <div className="text-white font-medium">{b.client_name}</div>
                  <div className="text-white/40 text-xs">{b.client_email}</div>
                  {b.client_phone && <div className="text-white/30 text-xs">{b.client_phone}</div>}
                </td>
                {/* Usługa + Stopień */}
                <td className="py-4 px-3">
                  <div className="text-white/80 text-xs leading-snug">{b.service_name || "-"}</div>
                  {editing[b.id] ? (
                    <input
                      className="mt-1 text-xs bg-[#111] border border-violet-600/40 text-violet-300 px-2 py-1 outline-none w-32"
                      placeholder="np. 2 STOPNIA"
                      value={editing[b.id].row_label}
                      onChange={e => setEditing(prev => ({ ...prev, [b.id]: { ...prev[b.id], row_label: e.target.value } }))}
                      onKeyDown={e => e.key === "Enter" && saveEdit(b.id)}
                    />
                  ) : (
                    <div className="flex items-center gap-1 mt-0.5">
                      {b.row_label
                        ? <span className="text-violet-400 text-xs">{b.row_label}</span>
                        : <button onClick={() => startEdit(b)} className="text-white/20 hover:text-violet-400 text-xs transition-colors flex items-center gap-1">
                            <Edit size={10} /> dodaj stopień
                          </button>
                      }
                    </div>
                  )}
                </td>
                {/* Cena */}
                <td className="py-4 px-3">
                  {editing[b.id] ? (
                    <div className="flex items-center gap-1">
                      <input
                        className="text-xs bg-[#111] border border-violet-600/40 text-white px-2 py-1 outline-none w-24"
                        placeholder="np. 600 zł"
                        value={editing[b.id].row_price}
                        onChange={e => setEditing(prev => ({ ...prev, [b.id]: { ...prev[b.id], row_price: e.target.value } }))}
                        onKeyDown={e => e.key === "Enter" && saveEdit(b.id)}
                      />
                      <button onClick={() => saveEdit(b.id)} className="text-green-400 hover:text-green-300 p-1"><Check size={12} /></button>
                      <button onClick={() => setEditing(prev => { const n={...prev}; delete n[b.id]; return n; })} className="text-white/30 hover:text-white p-1"><X size={12} /></button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      {b.row_price
                        ? <span className="font-outfit font-medium text-white text-sm">{b.row_price}</span>
                        : <button onClick={() => startEdit(b)} className="text-white/20 hover:text-violet-400 text-xs transition-colors flex items-center gap-1">
                            <Edit size={10} /> dodaj
                          </button>
                      }
                    </div>
                  )}
                </td>
                {/* Data & Godzina */}
                <td className="py-4 px-3">
                  <div className="text-white/70 text-xs">{b.date}</div>
                  <div className="text-white/50 text-xs">{b.time}</div>
                </td>
                {/* Uwagi */}
                <td className="py-4 px-3 max-w-[300px]">
                  {b.notes ? (
                    <div className="text-white/50 text-xs max-h-24 overflow-y-auto whitespace-pre-wrap break-words pr-2">
                      {b.notes}
                    </div>
                  ) : (
                    <span className="text-white/15 text-xs">brak</span>
                  )}
                </td>
                {/* Status */}
                <td className="py-4 px-3">
                  <select data-testid={`booking-status-${b.id}`} value={b.status}
                    onChange={e => updateStatus(b.id, e.target.value)}
                    className={`text-xs border px-2 py-1 font-outfit uppercase tracking-[0.1em] ${STATUS_COLORS[b.status] || "text-white/60 border-white/20"}`}
                    style={{ background: "#111111", colorScheme: "dark" }}>
                    {Object.entries(STATUS_PL).map(([v, l]) => <option key={v} value={v} style={{ background: "#111111" }}>{l}</option>)}
                  </select>
                </td>
                {/* Akcje */}
                <td className="py-4 px-3">
                  {b.status === "pending" && (
                    <button data-testid={`quick-confirm-${b.id}`} onClick={() => updateStatus(b.id, "confirmed")}
                      className="text-green-400/70 hover:text-green-400 p-1 mr-1" title="Potwierdź">
                      <Check size={14} />
                    </button>
                  )}
                  <button data-testid={`delete-booking-${b.id}`} onClick={() => del(b.id)} className="text-red-400/50 hover:text-red-400 p-1">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {bookings.length === 0 && <p className="text-white/30 text-center py-12">Brak rezerwacji.</p>}
      </div>
      {/* Pagination */}
      {bookings.length < total && (
        <div className="mt-4 flex items-center justify-between">
          <span className="text-white/30 text-xs">Wyświetlono {bookings.length} z {total}</span>
          <button
            onClick={() => {
              const newSkip = skip + LIMIT;
              setSkip(newSkip);
              load(newSkip, true, search);
            }}
            className="btn-secondary text-xs"
          >
            Pokaż więcej ({Math.min(LIMIT, total - bookings.length)})
            <ChevronRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

function TrainingsTab() {
  const [trainings, setTrainings] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", date: "", duration: 6, location: "", is_online: false, spots_total: 10, spots_available: 10, price: 0, deposit_amount: 0, program: "", image: "" });
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [expandedTraining, setExpandedTraining] = useState(null);
  const [view, setView] = useState("trainings");

  const load = () => {
    ax().get(`${API}/trainings`).then(r => setTrainings(r.data)).catch(console.error);
    ax().get(`${API}/training-purchases`).then(r => setPurchases(r.data)).catch(console.error);
  };
  useEffect(load, []);

  const save = async e => {
    e.preventDefault();
    if (editing) await ax().put(`${API}/trainings/${editing}`, form);
    else await ax().post(`${API}/trainings`, form);
    setShowForm(false); setEditing(null); load();
  };
  const edit = t => { setForm({ ...t }); setEditing(t.id); setShowForm(true); };
  const del = async id => { if (!window.confirm("Usunąć?")) return; await ax().delete(`${API}/trainings/${id}`); load(); };

  const updatePurchaseStatus = async (id, status) => {
    await ax().put(`${API}/training-purchases/${id}`, { status }); load();
  };

  const allParticipants = purchases.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <>
    <div data-testid="trainings-admin">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-anton text-2xl text-white uppercase">Szkolenia</h2>
        <div className="flex gap-3">
          {/* View toggle */}
          <div className="flex border border-white/10 overflow-hidden">
            <button onClick={() => setView("trainings")}
              className={`text-xs uppercase tracking-[0.12em] px-4 py-2 transition-colors ${view === "trainings" ? "bg-violet-600/20 text-white" : "text-white/40 hover:text-white"}`}>
              Szkolenia
            </button>
            <button onClick={() => setView("participants")}
              className={`text-xs uppercase tracking-[0.12em] px-4 py-2 transition-colors flex items-center gap-2 ${view === "participants" ? "bg-violet-600/20 text-white" : "text-white/40 hover:text-white"}`}>
              Uczestnicy
              {purchases.length > 0 && (
                <span className="bg-violet-600/30 text-violet-300 text-[10px] px-1.5 py-0.5 rounded-sm">{purchases.length}</span>
              )}
            </button>
          </div>
          {view === "trainings" && (
            <button data-testid="add-training-btn"
              onClick={() => { setForm({ title: "", description: "", date: "", duration: 6, location: "", is_online: false, spots_total: 10, spots_available: 10, price: 0, deposit_amount: 0, program: "", image: "" }); setEditing(null); setShowForm(true); }}
              className="btn-primary text-xs">
              <Plus size={14} /> Dodaj
            </button>
          )}
        </div>
      </div>

      {/* ── PARTICIPANTS VIEW ───────────────────────────────────────────────── */}
      {view === "participants" && (
        <div data-testid="participants-view">
          {purchases.length === 0 ? (
            <div className="py-20 text-center text-white/30 font-outfit text-sm">Brak zakupów szkoleń.</div>
          ) : (
            <>
              {/* Summary stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                <div className="border border-white/8 p-4" style={{ background: "#0A0A0A" }}>
                  <div className="font-anton text-3xl gradient-text">{purchases.filter(p => p.status !== "cancelled").length}</div>
                  <div className="text-white/40 text-xs mt-1">Aktywnych zgłoszeń</div>
                </div>
                <div className="border border-white/8 p-4" style={{ background: "#0A0A0A" }}>
                  <div className="font-anton text-3xl text-green-400">{purchases.filter(p => p.status === "paid").length}</div>
                  <div className="text-white/40 text-xs mt-1">Opłaconych</div>
                </div>
                <div className="border border-white/8 p-4" style={{ background: "#0A0A0A" }}>
                  <div className="font-anton text-3xl text-yellow-400">{purchases.filter(p => p.status === "pending").length}</div>
                  <div className="text-white/40 text-xs mt-1">Oczekujących</div>
                </div>
                <div className="border border-white/8 p-4" style={{ background: "#0A0A0A" }}>
                  <div className="font-anton text-3xl text-white">
                    {purchases.filter(p => p.status === "paid").reduce((sum, p) => sum + (p.amount || 0), 0)} zł
                  </div>
                  <div className="text-white/40 text-xs mt-1">Łączny przychód</div>
                </div>
              </div>

              {/* Group by training */}
              {trainings.map(t => {
                const tp = allParticipants.filter(p => p.training_id === t.id);
                const activeCount = tp.filter(p => p.status !== "cancelled").length;
                if (tp.length === 0) return null;
                const isExpanded = expandedTraining === t.id;
                return (
                  <div key={t.id} className="mb-6 border border-white/8" style={{ background: "#0A0A0A" }}>
                    {/* Training header */}
                    <button onClick={() => setExpandedTraining(isExpanded ? null : t.id)}
                      className="w-full flex justify-between items-center p-5 text-left hover:bg-white/2 transition-colors">
                      <div>
                        <div className="font-outfit font-semibold text-white">{t.title}</div>
                        <div className="text-white/40 text-xs mt-0.5">{t.date} · {t.location}</div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-white text-sm font-medium">
                            {activeCount} <span className="text-white/40 font-normal">aktywnych</span>
                            {tp.length > activeCount && <span className="text-white/20 text-xs ml-1">({tp.length - activeCount} anulowanych)</span>}
                          </div>
                          <div className="text-white/30 text-xs">{tp.filter(p => p.status === "paid").length} opłaconych</div>
                        </div>
                        <ChevronRight size={16} className={`text-white/30 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                      </div>
                    </button>

                    {/* Participants table */}
                    {isExpanded && (
                      <div className="border-t border-white/5 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-white/5">
                              {["Uczestnik", "Kontakt", "Status", "Kwota", "Data zgłoszenia"].map(h => (
                                <th key={h} className="text-left py-3 px-4 text-white/30 text-xs uppercase tracking-[0.12em] font-outfit">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {tp.map(p => (
                              <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.015] transition-colors">
                                <td className="py-4 px-4">
                                  <div className="font-outfit font-medium text-white">{p.client_name}</div>
                                </td>
                                <td className="py-4 px-4">
                                  <div className="space-y-1">
                                    <a href={`mailto:${p.client_email}`}
                                      className="text-violet-400 hover:text-white transition-colors text-sm flex items-center gap-1.5 group">
                                      <Mail size={12} className="flex-shrink-0" />
                                      <span className="group-hover:underline">{p.client_email}</span>
                                    </a>
                                    {p.client_phone ? (
                                      <a href={`tel:${p.client_phone}`}
                                        className="text-white/50 hover:text-white transition-colors text-xs flex items-center gap-1.5 group">
                                        <Phone size={11} className="flex-shrink-0" />
                                        <span className="group-hover:underline">{p.client_phone}</span>
                                      </a>
                                    ) : (
                                      <span className="text-white/20 text-xs">brak telefonu</span>
                                    )}
                                  </div>
                                </td>
                                <td className="py-4 px-4">
                                  <select value={p.status}
                                    onChange={e => updatePurchaseStatus(p.id, e.target.value)}
                                    className={`text-xs border px-2 py-1 font-outfit uppercase tracking-[0.1em] ${STATUS_COLORS[p.status] || "text-white/60 border-white/20"}`}
                                    style={{ background: "#111111", colorScheme: "dark" }}>
                                    {Object.entries({ pending: "Oczekuje", paid: "Opłacone", cancelled: "Anulowane" }).map(([v, l]) => (
                                      <option key={v} value={v} style={{ background: "#111111" }}>{l}</option>
                                    ))}
                                  </select>
                                </td>
                                <td className="py-4 px-4">
                                  <span className="font-outfit font-medium text-white">{p.amount} zł</span>
                                </td>
                                <td className="py-4 px-4 text-white/40 text-xs">
                                  {p.created_at ? new Date(p.created_at).toLocaleDateString("pl-PL") : "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {/* Export hint */}
                        <div className="px-4 py-3 border-t border-white/5 flex justify-between items-center">
                          <span className="text-white/25 text-xs">
                            Kliknij e-mail aby otworzyć klienta pocztowego · Kliknij telefon aby zadzwonić
                          </span>
                          <button
                            onClick={() => {
                              const rows = [["Imię i nazwisko", "Email", "Telefon", "Status", "Kwota", "Data"]];
                              tp.forEach(p => rows.push([p.client_name, p.client_email, p.client_phone || "", p.status, p.amount + " zł", p.created_at ? new Date(p.created_at).toLocaleDateString("pl-PL") : ""]));
                              const csv = rows.map(r => r.join(",")).join("\n");
                              navigator.clipboard.writeText(csv);
                              alert("Skopiowano do schowka jako CSV!");
                            }}
                            className="text-xs text-white/40 hover:text-white border border-white/10 px-3 py-1 transition-colors uppercase tracking-[0.1em]">
                            Kopiuj CSV
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Purchases without matching training */}
              {allParticipants.filter(p => !trainings.find(t => t.id === p.training_id)).length > 0 && (
                <div className="border border-white/5 p-4 mt-4">
                  <p className="text-white/30 text-xs mb-3">Inne zakupy (szkolenie archiwalne)</p>
                  {allParticipants.filter(p => !trainings.find(t => t.id === p.training_id)).map(p => (
                    <div key={p.id} className="flex justify-between items-center py-2 border-b border-white/5 text-sm">
                      <div>
                        <span className="text-white">{p.client_name}</span>
                        <a href={`mailto:${p.client_email}`} className="text-violet-400 text-xs ml-2 hover:text-white">{p.client_email}</a>
                        {p.client_phone && <span className="text-white/40 text-xs ml-2">{p.client_phone}</span>}
                      </div>
                      <span className="text-white/40 text-xs">{p.training_title} · {p.amount} zł</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── TRAININGS VIEW ──────────────────────────────────────────────────── */}
      {view === "trainings" && (
        <>
      {showForm && (
        <form data-testid="training-form" onSubmit={save} className="border border-violet-600/20 p-6 mb-8" style={{ background: "#0A0A0A" }}>
          <h3 className="font-outfit text-sm font-semibold text-white mb-1">{editing ? "Edytuj szkolenie" : "Dodaj nowe szkolenie"}</h3>
          <p className="text-white/30 text-xs mb-6">Wypełnij dane szkolenia. Pojawi się na stronie /szkolenia.</p>

          <div className="space-y-5">
            {/* Title */}
            <div>
              <label className="text-xs uppercase tracking-[0.12em] text-white/50 block mb-1.5">Nazwa szkolenia *</label>
              <input data-testid="training-title-input" placeholder="np. Masterclass: Kolory Kreatywne" required value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })} className="input-dark" />
            </div>

            {/* Date + Duration */}
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-[0.12em] text-white/50 block mb-1.5">Data szkolenia *</label>
                <input data-testid="training-date-input" type="date" required value={form.date}
                  onChange={e => setForm({ ...form, date: e.target.value })} className="input-dark"
                  style={{ colorScheme: "dark" }} />
                <p className="text-white/20 text-xs mt-1">Kiedy odbywa się szkolenie?</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.12em] text-white/50 block mb-1.5">Czas trwania *</label>
                <div className="flex items-center gap-2">
                  <input data-testid="training-duration-input" type="number" min="1" max="16" placeholder="6" required value={form.duration}
                    onChange={e => setForm({ ...form, duration: Number(e.target.value) })} className="input-dark flex-1" />
                  <span className="text-white/30 text-sm flex-shrink-0">godzin</span>
                </div>
                <p className="text-white/20 text-xs mt-1">Ile godzin trwa szkolenie? (np. 6, 8)</p>
              </div>
            </div>

            {/* Location + Online */}
            <div>
              <label className="text-xs uppercase tracking-[0.12em] text-white/50 block mb-1.5">Miejsce szkolenia *</label>
              <input data-testid="training-location-input" placeholder="np. Tarnów, ul. Reymonta 25  lub  Online / Live Stream" required value={form.location}
                onChange={e => setForm({ ...form, location: e.target.value })} className="input-dark" />
              <div className="flex items-center gap-3 mt-2">
                <input type="checkbox" id="is_online" checked={form.is_online}
                  onChange={e => setForm({ ...form, is_online: e.target.checked })} className="accent-violet-600 w-4 h-4" />
                <label htmlFor="is_online" className="text-white/50 text-sm">To szkolenie odbywa się online (przez internet)</label>
              </div>
            </div>

              <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-[0.12em] text-white/50 block mb-1.5">Liczba miejsc *</label>
                <input data-testid="training-spots-input" type="number" min="1" placeholder="10" value={form.spots_total}
                  onChange={e => setForm({ ...form, spots_total: Number(e.target.value), spots_available: Number(e.target.value) })}
                  className="input-dark" />
                <p className="text-white/20 text-xs mt-1">Maksymalna liczba uczestników</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.12em] text-white/50 block mb-1.5">Pełna cena za osobę *</label>
                <div className="flex items-center gap-2">
                  <input data-testid="training-price-input" type="number" min="0" placeholder="1200" required value={form.price}
                    onChange={e => setForm({ ...form, price: Number(e.target.value) })} className="input-dark flex-1" />
                  <span className="text-white/30 text-sm flex-shrink-0">zł</span>
                </div>
              </div>
            </div>

            {/* Deposit */}
            <div>
              <label className="text-xs uppercase tracking-[0.12em] text-white/50 block mb-1.5">Zadatek (opcjonalnie)</label>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <input data-testid="training-deposit-input" type="number" min="0" placeholder="500" value={form.deposit_amount || 0}
                    onChange={e => setForm({ ...form, deposit_amount: Number(e.target.value) })} className="input-dark flex-1" />
                  <span className="text-white/30 text-sm flex-shrink-0">zł</span>
                </div>
                <div className="flex items-center text-white/30 text-xs">
                  {(form.deposit_amount || 0) > 0 && form.price > 0
                    ? `Reszta na miejscu: ${form.price - (form.deposit_amount || 0)} zł`
                    : "0 = pełna cena od razu"}
                </div>
              </div>
              <p className="text-white/20 text-xs mt-1">
                Klientka płaci zadatek przed szkoleniem, reszta na miejscu. Zostaw 0 jeśli pełna cena z góry.
              </p>
            </div>

            {/* Description + Program */}
            <div>
              <label className="text-xs uppercase tracking-[0.12em] text-white/50 block mb-1.5">Opis szkolenia</label>
              <textarea placeholder="Opisz szkolenie: dla kogo jest, co osiągnie uczestnik, poziom zaawansowania..."
                value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                className="input-dark resize-none" rows={3} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.12em] text-white/50 block mb-1.5">Program / Agenda</label>
              <textarea data-testid="training-program-input"
                placeholder={"📍 Dla kogo?\nDla fryzjerów którzy chcą:\n• Rozjaśniać włosy świadomie\n• Zrozumieć działanie produktów\n\n💡 Co Cię czeka?\n\n🧪 Moduł 1: Tytuł\n• Punkt pierwszy\n• Punkt drugi\n\n🎨 Moduł 2: Tytuł\n• Punkt pierwszy"}
                value={form.program} onChange={e => setForm({ ...form, program: e.target.value })}
                className="input-dark resize-y" rows={10} />
              <p className="text-white/20 text-xs mt-1">
                Użyj emoji (📍 💡 🧪 🎨 🚨 🧴) jako nagłówki sekcji, • dla punktów. Enter = nowa linia. Obsługuje pełny opis z modułami.
              </p>
            </div>

            {/* Image */}
            <div>
              <label className="text-xs uppercase tracking-[0.12em] text-white/50 block mb-1.5">Zdjęcie (opcjonalnie)</label>
              <div className="flex gap-2">
                <input placeholder="https://... lub wybierz z biblioteki" value={form.image}
                  onChange={e => setForm({ ...form, image: e.target.value })} className="input-dark flex-1" />
                <button type="button" onClick={() => setShowPicker(true)}
                  className="btn-secondary text-xs flex-shrink-0 whitespace-nowrap">
                  <ImagePlus size={13} /> Biblioteka
                </button>
              </div>
              <p className="text-white/20 text-xs mt-1">Link URL lub wybierz z biblioteki zdjęć</p>
              {form.image && <img src={form.image} alt="" className="mt-2 h-20 object-cover border border-white/10" onError={e => e.target.style.display='none'} />}
            </div>
          </div>

          <div className="flex gap-3 mt-6 pt-5 border-t border-white/5">
            <button data-testid="training-save-btn" type="submit" className="btn-primary text-xs">
              {editing ? "Zapisz zmiany" : "Dodaj szkolenie"}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="btn-secondary text-xs">Anuluj</button>
          </div>
        </form>
      )}
      <div className="space-y-3">
        {trainings.map(t => {
          const tpCount = purchases.filter(p => p.training_id === t.id && p.status !== "cancelled").length;
          return (
            <div key={t.id} className="border border-white/8 p-5 flex justify-between items-center card-hover" style={{ background: "#0A0A0A" }}>
              <div>
                <div className="text-white font-outfit font-medium">{t.title}</div>
                <div className="text-white/40 text-xs mt-1">
                {t.date} · {t.duration}h ·{" "}
                <span className={t.spots_available <= 2 ? "text-red-400" : "text-green-400"}>
                  pozostało {t.spots_available}/{t.spots_total}
                </span>
                {" "}· {t.price} zł
              </div>
              </div>
              <div className="flex gap-2 items-center">
                {tpCount > 0 && (
                  <button onClick={() => { setView("participants"); setExpandedTraining(t.id); }}
                    className="text-xs text-violet-400 border border-violet-600/30 px-3 py-1.5 hover:bg-violet-600/10 transition-colors">
                    {tpCount} uczestnik(ów)
                  </button>
                )}
                <button data-testid={`edit-training-${t.id}`} onClick={() => edit(t)} className="text-white/40 hover:text-white p-2"><Edit size={14} /></button>
                <button data-testid={`delete-training-${t.id}`} onClick={() => del(t.id)} className="text-red-400/50 hover:text-red-400 p-2"><Trash2 size={14} /></button>
              </div>
            </div>
          );
        })}
      </div>
        </>
      )}
    </div>
    {showPicker && <MediaPickerModal onSelect={url => { setForm({ ...form, image: url }); setShowPicker(false); }} onClose={() => setShowPicker(false)} />}
    </>
  );
}

function ServicesTab() { return null; }

function GalleryTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ image_url: "", category: "koloryzacja", title: "", is_instagram: false });
  const [showForm, setShowForm] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const load = () => { ax().get(`${API}/gallery`).then(r => setItems(r.data)).catch(console.error); };
  useEffect(() => { load(); }, []);

  const save = async e => {
    e.preventDefault();
    await ax().post(`${API}/gallery`, form);
    setShowForm(false); load();
  };
  const del = async id => { if (!window.confirm("Usunąć?")) return; await ax().delete(`${API}/gallery/${id}`); load(); };

  return (
    <>
    <div data-testid="gallery-admin">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-anton text-2xl text-white uppercase">Galeria ({items.length})</h2>
        <button data-testid="add-gallery-btn" onClick={() => setShowForm(!showForm)} className="btn-primary text-xs"><Plus size={14} /> Dodaj</button>
      </div>
      {showForm && (
        <form data-testid="gallery-form" onSubmit={save} className="border border-violet-600/20 p-6 mb-8" style={{ background: "#0A0A0A" }}>
          <h3 className="font-outfit text-sm font-semibold text-white mb-1">Dodaj zdjęcie do galerii</h3>
          <p className="text-white/30 text-xs mb-6">Wklej link do zdjęcia i uzupełnij dane. Zdjęcie pojawi się w galerii na stronie.</p>

          <div className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.12em] text-white/50 block mb-1.5">Link do zdjęcia (URL) *</label>
              <div className="flex gap-2">
                <input data-testid="gallery-url-input" placeholder="https://... lub wybierz z biblioteki" required value={form.image_url}
                  onChange={e => setForm({ ...form, image_url: e.target.value })} className="input-dark flex-1" />
                <button type="button" onClick={() => setShowPicker(true)}
                  className="btn-secondary text-xs flex-shrink-0 whitespace-nowrap">
                  <ImagePlus size={13} /> Biblioteka
                </button>
              </div>
              <p className="text-white/20 text-xs mt-1">Wklej URL lub kliknij "Biblioteka" aby wybrać przesłane zdjęcie.</p>
              {form.image_url && (
                <img src={form.image_url} alt="" className="mt-2 h-24 object-cover border border-white/10"
                  onError={e => e.target.style.display='none'} />
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-[0.12em] text-white/50 block mb-1.5">Tytuł (opcjonalnie)</label>
                <input data-testid="gallery-title-input" placeholder="np. Violet Dream, Metamorfoza Vol. 1"
                  value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="input-dark" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.12em] text-white/50 block mb-1.5">Kategoria</label>
                <select data-testid="gallery-category-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                  className="w-full py-3 px-3 text-white text-sm font-outfit outline-none border border-white/15 focus:border-violet-600 transition-colors"
                  style={{ background: "#111111", colorScheme: "dark" }}>
                  <option value="blond"             style={{ background: "#111111" }}>Blond</option>
                  <option value="brunetka"          style={{ background: "#111111" }}>Brunetka</option>
                  <option value="bronde"            style={{ background: "#111111" }}>Bronde</option>
                  <option value="kolory kreatywne"  style={{ background: "#111111" }}>Kolory Kreatywne</option>
                  <option value="szkolenia"         style={{ background: "#111111" }}>Szkolenia</option>
                </select>
                <p className="text-white/20 text-xs mt-1">Używane do filtrowania w galerii</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 border border-white/8" style={{ background: "#111111" }}>
              <input type="checkbox" id="is_ig" checked={form.is_instagram}
                onChange={e => setForm({ ...form, is_instagram: e.target.checked })} className="accent-violet-600 w-4 h-4 flex-shrink-0" />
              <div>
                <label htmlFor="is_ig" className="text-white/70 text-sm cursor-pointer">To zdjęcie pochodzi z Instagrama</label>
                <p className="text-white/25 text-xs mt-0.5">Zaznacz jeśli chcesz żeby pojawiło się w sekcji Instagram na stronie głównej</p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6 pt-5 border-t border-white/5">
            <button data-testid="gallery-save-btn" type="submit" className="btn-primary text-xs">Dodaj do galerii</button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary text-xs">Anuluj</button>
          </div>
        </form>
      )}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {items.map(item => (
          <div key={item.id} data-testid={`gallery-admin-item-${item.id}`} className="relative group aspect-square overflow-hidden">
            <img
              src={item.thumbnail_url || item.image_url}
              alt={item.title}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button onClick={() => del(item.id)} data-testid={`delete-gallery-${item.id}`} className="text-red-400 p-1"><Trash2 size={14} /></button>
            </div>
            {item.is_instagram && <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-violet-500" />}
          </div>
        ))}
      </div>
    </div>
    {showPicker && <MediaPickerModal onSelect={url => { setForm(f => ({ ...f, image_url: url })); setShowPicker(false); }} onClose={() => setShowPicker(false)} />}
  </>
  );
}

function PartnersTab() {
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState({ name: "", logo_url: "", website: "", description: "" });
  const [showForm, setShowForm] = useState(false);

  const load = () => { ax().get(`${API}/partners`).then(r => setPartners(r.data)).catch(console.error); };
  useEffect(() => { load(); }, []);

  const save = async e => {
    e.preventDefault();
    await ax().post(`${API}/partners`, form);
    setShowForm(false); load();
  };
  const del = async id => { if (!window.confirm("Usunąć?")) return; await ax().delete(`${API}/partners/${id}`); load(); };

  return (
    <div data-testid="partners-admin">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-anton text-2xl text-white uppercase">Partnerzy</h2>
        <button data-testid="add-partner-btn" onClick={() => setShowForm(!showForm)} className="btn-primary text-xs"><Plus size={14} /> Dodaj</button>
      </div>
      {showForm && (
        <form onSubmit={save} className="border border-violet-600/20 p-6 mb-8" style={{ background: "#0A0A0A" }}>
          <h3 className="font-outfit text-sm font-semibold text-white mb-1">Dodaj partnera / markę</h3>
          <p className="text-white/30 text-xs mb-6">Partnerzy wyświetlają się w sekcji "Współpracuję z" na stronie głównej.</p>

          <div className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.12em] text-white/50 block mb-1.5">Nazwa marki / firmy *</label>
              <input placeholder="np. Wella Professionals, Olaplex" required value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })} className="input-dark" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.12em] text-white/50 block mb-1.5">Krótki opis (opcjonalnie)</label>
              <input placeholder="np. Oficjalny partner, Certyfikowany specjalista"
                value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="input-dark" />
              <p className="text-white/20 text-xs mt-1">Jedna linijka tekstu pod nazwą marki</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.12em] text-white/50 block mb-1.5">Strona internetowa (opcjonalnie)</label>
              <input placeholder="https://www.wella.com" value={form.website}
                onChange={e => setForm({ ...form, website: e.target.value })} className="input-dark" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.12em] text-white/50 block mb-1.5">Logo (opcjonalnie)</label>
              <input placeholder="https://..." value={form.logo_url}
                onChange={e => setForm({ ...form, logo_url: e.target.value })} className="input-dark" />
              <p className="text-white/20 text-xs mt-1">Link URL do logo marki. Na razie wyświetlana jest tylko nazwa tekstowa.</p>
            </div>
          </div>

          <div className="flex gap-3 mt-6 pt-5 border-t border-white/5">
            <button data-testid="partner-save-btn" type="submit" className="btn-primary text-xs">Dodaj partnera</button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary text-xs">Anuluj</button>
          </div>
        </form>
      )}
      <div className="space-y-3">
        {partners.map(p => (
          <div key={p.id} className="border border-white/8 p-5 flex justify-between items-center" style={{ background: "#0A0A0A" }}>
            <div>
              <div className="text-white font-outfit font-medium">{p.name}</div>
              <div className="text-white/40 text-xs mt-0.5">{p.description}</div>
            </div>
            <button onClick={() => del(p.id)} data-testid={`delete-partner-${p.id}`} className="text-red-400/50 hover:text-red-400 p-2"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function TestimonialsTab() { return null; }

// ── Shared MediaPickerModal ────────────────────────────────────────────────────
function MediaPickerModal({ onSelect, onClose }) {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  useEffect(() => {
    ax().get(`${API}/media`).then(r => setImages(r.data)).catch(console.error);
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await ax().post(`${API}/media/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImages(prev => [data, ...prev]);
    } catch (err) { alert(err.response?.data?.detail || "Błąd uploadu"); }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-4 border-b border-white/8">
          <h3 className="font-outfit font-semibold text-white text-sm">Biblioteka zdjęć — wybierz</h3>
          <button onClick={onClose}><X size={16} className="text-white/50 hover:text-white" /></button>
        </div>
        <div className="p-4 border-b border-white/5">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            className="btn-primary text-xs disabled:opacity-50">
            <Upload size={13} /> {uploading ? "Przesyłanie..." : "Prześlij nowe zdjęcie"}
          </button>
        </div>
        <div className="overflow-y-auto p-4 grid grid-cols-3 sm:grid-cols-4 gap-2 flex-1">
          {images.length === 0 && <p className="text-white/30 text-xs col-span-4">Brak zdjęć. Prześlij pierwsze!</p>}
          {images.map((img, i) => (
            <button key={i} onClick={() => onSelect(img.url)}
              className="aspect-square overflow-hidden border border-white/10 hover:border-violet-500 transition-colors group relative">
              <img
                src={img.thumbnail_url || img.url}
                alt=""
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-violet-600/0 group-hover:bg-violet-600/20 transition-colors flex items-center justify-center">
                <Check size={20} className="text-white opacity-0 group-hover:opacity-100" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function MediaLibraryTab() {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(null);
  const fileRef = React.useRef(null);

  const load = () => { ax().get(`${API}/media`).then(r => setImages(r.data)).catch(console.error); };
  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData(); fd.append("file", file);
      try {
        await ax().post(`${API}/media/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (err) { alert(`Błąd: ${err.response?.data?.detail || file.name}`); }
    }
    setUploading(false); load();
  };

  const copyUrl = (url, idx) => {
    navigator.clipboard.writeText(url).then(() => { setCopied(idx); setTimeout(() => setCopied(null), 2000); });
  };

  const del = async (filename) => {
    if (!window.confirm("Usunąć to zdjęcie?")) return;
    await ax().delete(`${API}/media/${filename}`); load();
  };

  return (
    <div data-testid="media-library">
      <div className="flex justify-between items-center mb-2">
        <h2 className="font-anton text-2xl text-white uppercase">Biblioteka Zdjęć</h2>
        <div>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-primary text-xs disabled:opacity-50">
            <Upload size={13} /> {uploading ? "Przesyłanie..." : "Prześlij zdjęcia"}
          </button>
        </div>
      </div>
      <p className="text-white/30 text-xs mb-6">
        Prześlij zdjęcia z urządzenia — możesz je później wybrać w formularzach Galerii i Szkoleń. Max 10 MB, formaty: jpg, png, webp, gif.
      </p>

      {images.length === 0 ? (
        <div className="py-20 text-center border border-white/5 border-dashed" style={{ background: "#0A0A0A" }}>
          <Upload size={32} className="text-white/20 mx-auto mb-4" />
          <p className="text-white/30 text-sm">Brak zdjęć. Kliknij "Prześlij zdjęcia" aby dodać.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {images.map((img, i) => (
            <div
              key={img.filename || i}
              className="group relative aspect-square border border-white/8 overflow-hidden"
              style={{ background: "#0A0A0A" }}
            >
            <img
              src={img.thumbnail_url || img.url}
              alt={img.filename}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />

              <div className="absolute bottom-0 left-0 right-0 p-2 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-white/50 text-[10px] truncate">
                  {img.filename}
                </p>

                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => copyUrl(img.url, i)}
                    className="text-white/70 hover:text-white text-xs"
                  >
                    {copied === i ? "Skopiowano" : "Kopiuj"}
                  </button>

                  <button
                    onClick={() => del(img.filename)}
                    className="text-red-400/70 hover:text-red-400 text-xs"
                  >
                    Usuń
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmailTemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ subject: "", body_html: "", name: "", is_active: true });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testMsg, setTestMsg] = useState("");

  const load = () => { ax().get(`${API}/email-templates`).then(r => setTemplates(r.data)).catch(console.error); };
  useEffect(() => { load(); }, []);

  const startEdit = (t) => {
    setEditing(t.id);
    setEditForm({ subject: t.subject, body_html: t.body_html, name: t.name, is_active: t.is_active });
    setTestMsg("");
  };

  const save = async () => {
    setSaving(true);
    try { await ax().put(`${API}/email-templates/${editing}`, editForm); load(); setEditing(null); }
    finally { setSaving(false); }
  };

  const sendTest = async (id) => {
    setTesting(id); setTestMsg("");
    try {
      const { data } = await ax().post(`${API}/email-templates/${id}/test`);
      setTestMsg(`Wysłano testowy e-mail na ${data.sent_to}`);
    } catch (e) {
      setTestMsg(e.response?.data?.detail || "Błąd wysyłania");
    } finally { setTesting(null); }
  };

  const TEMPLATE_LABELS = {
    booking_received: { emoji: "📨", color: "text-blue-400", group: "Rezerwacje" },
    booking_confirmed: { emoji: "✅", color: "text-green-400", group: "Rezerwacje" },
    booking_cancelled: { emoji: "❌", color: "text-red-400", group: "Rezerwacje" },
    booking_new_admin: { emoji: "🔔", color: "text-yellow-400", group: "Rezerwacje" },
    training_purchased: { emoji: "📋", color: "text-blue-400", group: "Szkolenia" },
    training_confirmed: { emoji: "💚", color: "text-green-400", group: "Szkolenia" },
    training_cancelled: { emoji: "🚫", color: "text-red-400", group: "Szkolenia" },
    training_new_admin: { emoji: "🔔", color: "text-yellow-400", group: "Szkolenia" },
    contact_new_admin: { emoji: "📩", color: "text-violet-400", group: "Kontakt" },
  };

  const grouped = templates.reduce((acc, t) => {
    const g = TEMPLATE_LABELS[t.key]?.group || "Inne";
    if (!acc[g]) acc[g] = [];
    acc[g].push(t);
    return acc;
  }, {});

  return (
    <div data-testid="email-templates-admin">
      <div className="flex justify-between items-center mb-2">
        <h2 className="font-anton text-2xl text-white uppercase">Szablony E-mail</h2>
      </div>
      <p className="text-white/30 text-xs mb-6">
        Wysyłano z: <span className="text-violet-400">fryzjerartystamilena@gmail.com</span>
        {" · "}Kliknij <strong className="text-white/50">Edytuj</strong> aby zmienić temat lub treść.
        Zmienne: <code className="text-violet-400 bg-white/5 px-1 rounded">{"{{client_name}}"}</code> zostaną zastąpione danymi.
      </p>

      {testMsg && (
        <div className={`mb-4 p-3 text-xs border ${testMsg.startsWith("Wysłano") ? "border-green-500/30 text-green-400" : "border-red-500/30 text-red-400"}`}
          style={{ background: testMsg.startsWith("Wysłano") ? "rgba(34,197,94,0.05)" : "rgba(239,68,68,0.05)" }}>
          {testMsg}
        </div>
      )}

      {Object.entries(grouped).map(([group, items]) => (
        <div key={group} className="mb-8">
          <h3 className="font-outfit text-xs uppercase tracking-[0.2em] text-white/30 mb-3 pb-2 border-b border-white/5">{group}</h3>
          <div className="space-y-2">
            {items.map(t => {
              const meta = TEMPLATE_LABELS[t.key] || {};
              const isEditing = editing === t.id;
              return (
                <div key={t.id}>
                  {isEditing ? (
                    <div className="border border-violet-600/25 p-5" style={{ background: "#0A0A0A" }}>
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span>{meta.emoji}</span>
                            <span className={`text-sm font-outfit font-medium ${meta.color}`}>{t.name}</span>
                          </div>
                          <div className="text-white/20 text-xs">Dostępne zmienne: {t.variables?.join(", ")}</div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                          <button onClick={save} disabled={saving} className="btn-primary text-xs disabled:opacity-50">
                            {saving ? "Zapis..." : "Zapisz"}
                          </button>
                          <button onClick={() => setEditing(null)} className="btn-secondary text-xs">Anuluj</button>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="text-xs uppercase tracking-[0.12em] text-white/40 block mb-1.5">Temat e-maila</label>
                          <input value={editForm.subject}
                            onChange={e => setEditForm(f => ({ ...f, subject: e.target.value }))}
                            className="input-dark w-full text-sm" placeholder="Temat..." />
                          <p className="text-white/20 text-xs mt-1">Możesz użyć: {t.variables?.map(v => `{{${v}}}`).join(", ")}</p>
                        </div>
                        <div>
                          <label className="text-xs uppercase tracking-[0.12em] text-white/40 block mb-1.5">Treść HTML</label>
                          <textarea value={editForm.body_html}
                            onChange={e => setEditForm(f => ({ ...f, body_html: e.target.value }))}
                            className="input-dark resize-y w-full font-mono text-xs"
                            rows={12} style={{ lineHeight: 1.5 }} />
                          <p className="text-white/20 text-xs mt-1">Edytujesz kod HTML. Zmienne: {"{{variable}}"} — zostaną zastąpione danymi.</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <input type="checkbox" id={`active-${t.id}`} checked={editForm.is_active}
                            onChange={e => setEditForm(f => ({ ...f, is_active: e.target.checked }))}
                            className="accent-violet-600 w-4 h-4" />
                          <label htmlFor={`active-${t.id}`} className="text-white/60 text-sm">
                            Wysyłaj ten e-mail automatycznie
                          </label>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={`border px-4 py-3 flex justify-between items-center ${t.is_active ? "border-white/8" : "border-white/3 opacity-50"}`}
                      style={{ background: "#0A0A0A" }}>
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm">{meta.emoji}</span>
                          <span className="font-outfit text-sm text-white">{t.name}</span>
                          {!t.is_active && <span className="text-xs text-yellow-500/50 border border-yellow-500/20 px-1.5 py-0.5">Wyłączony</span>}
                        </div>
                        <div className="text-white/30 text-xs ml-6 truncate max-w-md">{t.subject}</div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0 ml-4">
                        <button onClick={() => sendTest(t.id)} disabled={testing === t.id}
                          className="text-xs text-violet-400 border border-violet-600/30 px-3 py-1.5 hover:bg-violet-600/10 transition-colors flex items-center gap-1.5 disabled:opacity-40"
                          title="Wyślij testowy e-mail">
                          <Send size={12} /> {testing === t.id ? "Wysyłam..." : "Test"}
                        </button>
                        <button onClick={() => startEdit(t)} className="text-white/40 hover:text-white p-2 transition-colors">
                          <Edit size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function CennikTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => { ax().get(`${API}/pricing/all`).then(r => setItems(r.data)).catch(console.error); };
  useEffect(() => { load(); }, []);

  const startEdit = (item) => {
    setEditing(item.id);
    setEditForm({
      subtitle: item.subtitle,
      rows: item.rows.map(r => ({ ...r })),
      durations: item.durations ? [...item.durations] : item.rows.map(() => 60),
      notes: [...item.notes],
      warning: item.warning || "",
      is_active: item.is_active,
      blocks_slots: item.blocks_slots ?? true,
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await ax().post(`${API}/pricing`, editForm);
      } else {
        await ax().put(`${API}/pricing/${editing}`, editForm);
      }
      setEditing(null); load();
    }
    finally { setSaving(false); }
  };

  const addRow = () => setEditForm(f => ({ ...f, rows: [...f.rows, { label: "", price: "" }], durations: [...(f.durations||[]), 60] }));
  const removeRow = (i) => setEditForm(f => ({ ...f, rows: f.rows.filter((_, j) => j !== i), durations: (f.durations||[]).filter((_, j) => j !== i) }));
  const updateRow = (i, field, val) => setEditForm(f => ({ ...f, rows: f.rows.map((r, j) => j === i ? { ...r, [field]: val } : r) }));
  const updateDuration = (i, val) => setEditForm(f => { const d = [...(f.durations||[])]; d[i] = Number(val)||60; return { ...f, durations: d }; });

  const startAdd = () => {
    setEditing("new");
    setEditForm({
      subtitle: "Nowa usługa",
      rows: [{ label: "", price: "" }],
      durations: [60],
      notes: [],
      warning: "",
      is_active: true,
      blocks_slots: true,
    });
  };

  return (
    <div data-testid="cennik-admin">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-anton text-2xl text-white uppercase">Cennik ({items.length} usług)</h2>
          <p className="text-white/30 text-xs mt-1">Edytuj ceny i opisy. Widoczne na stronie /cennik</p>
        </div>
        <div className="flex items-center gap-3">
          <a href="/cennik" target="_blank" className="text-white/40 hover:text-white text-xs uppercase tracking-[0.15em] border border-white/10 px-3 py-2">
            Podgląd ↗
          </a>
          <button data-testid="add-pricing-btn" onClick={startAdd} className="btn-primary text-xs">Dodaj</button>
        </div>
      </div>

      <div className="space-y-3">
        {editing === "new" && editForm && (
          <div className="border border-violet-600/30 p-6" style={{ background: "#0A0A0A" }}>
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-outfit font-semibold text-white">Nowa usługa</h3>
              <div className="flex gap-2">
                <button onClick={save} disabled={saving} className="btn-primary text-xs disabled:opacity-50">
                  {saving ? "Zapisuję..." : "Zapisz"}
                </button>
                <button onClick={() => setEditing(null)} className="btn-secondary text-xs">Anuluj</button>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-xs uppercase tracking-[0.12em] text-white/40 block mb-1.5">Nazwa usługi</label>
              <input value={editForm.subtitle} onChange={e => setEditForm(f => ({ ...f, subtitle: e.target.value }))}
                className="input-dark w-full" placeholder="Nazwa usługi" />
            </div>

            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <label className="text-xs uppercase tracking-[0.12em] text-white/40">Cennik (etykieta + cena)</label>
                <button onClick={() => setEditForm(f => ({ ...f, rows: [...f.rows, { label: "", price: "" }], durations: [...(f.durations||[]), 60] }))} className="text-violet-400 text-xs hover:text-white">+ Dodaj wiersz</button>
              </div>
              <div className="mb-1">
                <div className="grid grid-cols-[1fr_7rem_6rem_1.5rem] gap-2 mb-1">
                  <span className="text-white/25 text-xs">Etykieta</span>
                  <span className="text-white/25 text-xs">Cena</span>
                  <span className="text-violet-400 text-xs flex items-center gap-1"><Clock size={10}/> Czas (min)</span>
                  <span></span>
                </div>
                {editForm.rows.map((row, i) => (
                  <div key={i} className="grid grid-cols-[1fr_7rem_6rem_1.5rem] gap-2 items-center mb-1.5">
                    <input value={row.label} onChange={e => setEditForm(f => ({ ...f, rows: f.rows.map((r,j)=> j===i ? { ...r, label: e.target.value } : r) }))}
                      placeholder="np. 1 STOPNIA" className="input-dark text-sm" />
                    <input value={row.price} onChange={e => setEditForm(f => ({ ...f, rows: f.rows.map((r,j)=> j===i ? { ...r, price: e.target.value } : r) }))}
                      placeholder="400 zł" className="input-dark text-sm" />
                    <input type="number" min="15" step="15"
                      value={(editForm.durations||[])[i] || 60}
                      onChange={e => setEditForm(f => { const d = [...(f.durations||[])]; d[i] = Number(e.target.value)||60; return { ...f, durations: d }; })}
                      className="input-dark text-sm text-violet-300" placeholder="min" />
                    <button onClick={() => setEditForm(f => ({ ...f, rows: f.rows.filter((_, j) => j !== i), durations: (f.durations||[]).filter((_, j) => j !== i) }))} className="text-red-400/50 hover:text-red-400">
                      <X size={13} />
                    </button>
                  </div>
                ))}
                <p className="text-white/20 text-[11px] mt-1">Czas trwania usługi w minutach — używany do blokowania kalendarza rezerwacji.</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-xs uppercase tracking-[0.12em] text-white/40 block mb-1.5">Uwagi (* pod cennikiem)</label>
              <textarea value={editForm.notes.join("\n")}
                onChange={e => setEditForm(f => ({ ...f, notes: e.target.value.split("\n").filter(Boolean) }))}
                className="input-dark resize-none w-full" rows={3}
                placeholder="Każda linia = osobna uwaga" />
            </div>

            <div className="mb-4">
              <label className="text-xs uppercase tracking-[0.12em] text-white/40 block mb-1.5">Ostrzeżenie (żółty box)</label>
              <input value={editForm.warning} onChange={e => setEditForm(f => ({ ...f, warning: e.target.value }))}
                className="input-dark w-full" placeholder="np. Konsultacja obowiązkowa..." />
            </div>

            <div className="flex items-center gap-3">
              <input type="checkbox" id={`active-new`} checked={editForm.is_active}
                onChange={e => setEditForm(f => ({ ...f, is_active: e.target.checked }))} className="accent-violet-600 w-4 h-4" />
              <label htmlFor={`active-new`} className="text-white/60 text-sm">Widoczna na stronie</label>
              <div className="ml-6 flex items-center gap-3">
                <input type="checkbox" id={`blocks-new`} checked={!!editForm.blocks_slots}
                  onChange={e => setEditForm(f => ({ ...f, blocks_slots: e.target.checked }))} className="accent-violet-600 w-4 h-4" />
                <label htmlFor={`blocks-new`} className="text-white/60 text-sm">Blokuje godziny (rezerwacja zablokuje sloty)</label>
              </div>
            </div>
          </div>
        )}
        {items.map((item, idx) => (
          <div key={item.id}>
            {editing === item.id && editForm ? (
              <div className="border border-violet-600/30 p-6" style={{ background: "#0A0A0A" }}>
                <div className="flex justify-between items-start mb-4">
                  <h3 className="font-outfit font-semibold text-white">{item.subtitle}</h3>
                  <div className="flex gap-2">
                    <button onClick={save} disabled={saving} className="btn-primary text-xs disabled:opacity-50">
                      {saving ? "Zapisuję..." : "Zapisz"}
                    </button>
                    <button onClick={() => setEditing(null)} className="btn-secondary text-xs">Anuluj</button>
                  </div>
                </div>

                {/* Subtitle */}
                <div className="mb-4">
                  <label className="text-xs uppercase tracking-[0.12em] text-white/40 block mb-1.5">Nazwa usługi</label>
                  <input value={editForm.subtitle} onChange={e => setEditForm(f => ({ ...f, subtitle: e.target.value }))}
                    className="input-dark w-full" placeholder="Nazwa usługi" />
                </div>

                {/* Rows */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-xs uppercase tracking-[0.12em] text-white/40">Cennik (etykieta + cena)</label>
                    <button onClick={addRow} className="text-violet-400 text-xs hover:text-white">+ Dodaj wiersz</button>
                  </div>
                  <div className="mb-1">
                    <div className="grid grid-cols-[1fr_7rem_6rem_1.5rem] gap-2 mb-1">
                      <span className="text-white/25 text-xs">Etykieta</span>
                      <span className="text-white/25 text-xs">Cena</span>
                      <span className="text-violet-400 text-xs flex items-center gap-1"><Clock size={10}/> Czas (min)</span>
                      <span></span>
                    </div>
                    {editForm.rows.map((row, i) => (
                      <div key={i} className="grid grid-cols-[1fr_7rem_6rem_1.5rem] gap-2 items-center mb-1.5">
                        <input value={row.label} onChange={e => updateRow(i, "label", e.target.value)}
                          placeholder="np. 1 STOPNIA" className="input-dark text-sm" />
                        <input value={row.price} onChange={e => updateRow(i, "price", e.target.value)}
                          placeholder="400 zł" className="input-dark text-sm" />
                        <input type="number" min="15" step="15"
                          value={(editForm.durations||[])[i] || 60}
                          onChange={e => updateDuration(i, e.target.value)}
                          className="input-dark text-sm text-violet-300" placeholder="min" />
                        <button onClick={() => removeRow(i)} className="text-red-400/50 hover:text-red-400">
                          <X size={13} />
                        </button>
                      </div>
                    ))}
                    <p className="text-white/20 text-[11px] mt-1">Czas trwania usługi w minutach — używany do blokowania kalendarza rezerwacji.</p>
                  </div>
                </div>

                {/* Notes */}
                <div className="mb-4">
                  <label className="text-xs uppercase tracking-[0.12em] text-white/40 block mb-1.5">Uwagi (* pod cennikiem)</label>
                  <textarea value={editForm.notes.join("\n")}
                    onChange={e => setEditForm(f => ({ ...f, notes: e.target.value.split("\n").filter(Boolean) }))}
                    className="input-dark resize-none w-full" rows={3}
                    placeholder="Każda linia = osobna uwaga" />
                </div>

                {/* Warning */}
                <div className="mb-4">
                  <label className="text-xs uppercase tracking-[0.12em] text-white/40 block mb-1.5">Ostrzeżenie (żółty box)</label>
                  <input value={editForm.warning} onChange={e => setEditForm(f => ({ ...f, warning: e.target.value }))}
                    className="input-dark w-full" placeholder="np. Konsultacja obowiązkowa..." />
                </div>

                <div className="flex items-center gap-3">
                  <input type="checkbox" id={`active-${item.id}`} checked={editForm.is_active}
                    onChange={e => setEditForm(f => ({ ...f, is_active: e.target.checked }))} className="accent-violet-600 w-4 h-4" />
                  <label htmlFor={`active-${item.id}`} className="text-white/60 text-sm">Widoczna na stronie</label>
                  <div className="ml-6 flex items-center gap-3">
                    <input type="checkbox" id={`blocks-${item.id}`} checked={!!editForm.blocks_slots}
                      onChange={e => setEditForm(f => ({ ...f, blocks_slots: e.target.checked }))} className="accent-violet-600 w-4 h-4" />
                    <label htmlFor={`blocks-${item.id}`} className="text-white/60 text-sm">Blokuje godziny (rezerwacja zablokuje sloty)</label>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border border-white/8 px-5 py-4 flex justify-between items-center" style={{ background: "#0A0A0A" }}>
                <div>
                  <div className="flex items-center gap-3">
                    <span className="text-white/20 text-xs w-6">{String(idx+1).padStart(2,"0")}</span>
                    <span className={`font-outfit font-medium text-sm ${item.is_active ? "text-white" : "text-white/30"}`}>{item.subtitle}</span>
                    {!item.is_active && <span className="text-xs text-yellow-500/50 border border-yellow-500/20 px-2 py-0.5">Ukryta</span>}
                  </div>
                  <div className="text-white/30 text-xs mt-1 ml-9">
                    {item.rows.slice(0, 3).map(r => `${r.label}: ${r.price}`).join(" · ")}
                    {item.rows.length > 3 ? ` · +${item.rows.length - 3}` : ""}
                  </div>
                </div>
                <button data-testid={`edit-pricing-${item.id}`} onClick={() => startEdit(item)}
                  className="text-white/40 hover:text-white p-2 transition-colors">
                  <Edit size={14} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const MONTHS_PL = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
];

const DAYS_PL = ["Pon", "Wt", "Śr", "Czw", "Pt", "Sob", "Nie"];

function getCalendarDays(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDay = (first.getDay() + 6) % 7;
  const days = [];

  for (let i = 0; i < startDay; i++) days.push(null);
  for (let d = 1; d <= last.getDate(); d++) days.push(d);

  return days;
}

function CalendarTab() {
  const [workingHours, setWorkingHours] = useState([]);
  const [closedDates, setClosedDates] = useState([]);
  const [specialDates, setSpecialDates] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [blockedTotal, setBlockedTotal] = useState(0);
  const [blockedSkip, setBlockedSkip] = useState(0);
  const BLOCKED_LIMIT = 200;
  const [newClosed, setNewClosed] = useState({ date: "", date_end: "", reason: "" });
  const [newSpecial, setNewSpecial] = useState({ date: "", start_time: "09:00", end_time: "18:00", slot_duration: 30, reason: "" });
  const [newBlock, setNewBlock] = useState({ date_from: "", date_to: "", time_from: "09:00", time_to: "18:00", reason: "Przeniesione z zeszytu", days_of_week: null });
  const [saving, setSaving] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [regenMsg, setRegenMsg] = useState("");
  const [blocking, setBlocking] = useState(false);
  const [blockMsg, setBlockMsg] = useState("");

  const today = new Date();
  const [bookingsPreview, setBookingsPreview] = useState([]);
  const [previewYear, setPreviewYear] = useState(today.getFullYear());
  const [previewMonth, setPreviewMonth] = useState(today.getMonth());
  const [previewDate, setPreviewDate] = useState(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`
  );

  const load = () => {
    ax().get(`${API}/working-hours`).then(r => setWorkingHours(r.data)).catch(console.error);
    ax().get(`${API}/closed-dates`).then(r => setClosedDates(r.data)).catch(console.error);
    ax().get(`${API}/special-date-hours`).then(r => setSpecialDates(r.data)).catch(console.error);
    ax().get(`${API}/slots/blocked?skip=${blockedSkip}&limit=${BLOCKED_LIMIT}`)
      .then(r => {
        const data = r.data;
        const items = data.items || data;
        setBlockedTotal(data.total || items.length);
        setBlocked(items);
      }).catch(console.error);
    ax().get(`${API}/bookings?skip=0&limit=1000`)
      .then(r => {
        const data = r.data;
        setBookingsPreview(data.items || data);
      })
      .catch(console.error);
  };
  useEffect(() => { load(); }, []);

  const updateDay = async (dow, field, value) => {
    const cur = workingHours.find(w => w.day_of_week === dow);
    if (!cur) return;
    const updated = { ...cur, [field]: value };
    setWorkingHours(prev => prev.map(w => w.day_of_week === dow ? { ...w, [field]: value } : w));
    setSaving(dow);
    try { await ax().put(`${API}/working-hours/${dow}`, { is_working: updated.is_working, start_time: updated.start_time, end_time: updated.end_time, slot_duration: updated.slot_duration }); }
    catch (e) { console.error(e); }
    finally { setSaving(null); }
  };

  const addClosed = async e => {
    e.preventDefault();
    if (!newClosed.date) return;
    const payload = { date: newClosed.date, reason: newClosed.reason };
    if (newClosed.date_end && newClosed.date_end >= newClosed.date) payload.date_end = newClosed.date_end;
    await ax().post(`${API}/closed-dates`, payload);
    setNewClosed({ date: "", date_end: "", reason: "" }); load();
  };
  const removeClosed = async id => { await ax().delete(`${API}/closed-dates/${id}`); load(); };

  const addSpecial = async e => {
    e.preventDefault();
    await ax().post(`${API}/special-date-hours`, newSpecial);
    setNewSpecial({ date: "", start_time: "09:00", end_time: "18:00", slot_duration: 30, reason: "" }); load();
  };
  const removeSpecial = async id => { await ax().delete(`${API}/special-date-hours/${id}`); load(); };

  const addBlock = async e => {
    e.preventDefault(); setBlocking(true); setBlockMsg("");
    try {
      const payload = { ...newBlock };
      if (!payload.date_to) payload.date_to = payload.date_from;
      const { data } = await ax().post(`${API}/slots/block-range`, payload);
      setBlockMsg(`Zablokowano ${data.blocked_slots} slotów.`);
      setNewBlock({ date_from: "", date_to: "", time_from: "09:00", time_to: "18:00", reason: "Przeniesione z zeszytu", days_of_week: null });
      load();
    } catch (e) { setBlockMsg("Błąd: " + (e.response?.data?.detail || e.message)); }
    finally { setBlocking(false); }
  };
  const removeBlock = async id => { await ax().delete(`${API}/slots/blocked/${id}`); load(); };

  const regenerate = async () => {
    setRegenerating(true); setRegenMsg("");
    try {
      const { data } = await ax().post(`${API}/slots/regenerate`);
      setRegenMsg(`Wygenerowano ${data.slots_created} terminów na 365 dni do przodu.`);
    } catch (e) { setRegenMsg("Błąd podczas generowania."); }
    finally { setRegenerating(false); }
  };

  const INTERVAL_OPTIONS = [15, 30, 45, 60];
  const DOW_LABELS = ["Pon", "Wt", "Śr", "Czw", "Pt", "Sob", "Nie"];

  const previewDays = getCalendarDays(previewYear, previewMonth);

  const previewDateStr = (day) =>
    `${previewYear}-${String(previewMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  const bookingsForPreviewDate = (date) =>
    bookingsPreview
      .filter(b =>
        b.date === date &&
        !String(b.client_name || "").includes("Przeniesione z zeszytu")
      )
      .sort((a, b) => (a.time || "").localeCompare(b.time || ""));

  const selectedPreviewBookings = bookingsForPreviewDate(previewDate);

  const hasPreviewBooking = (day) => {
    if (!day) return false;
    return bookingsForPreviewDate(previewDateStr(day)).some(b => b.status !== "cancelled");
  };

  const hasPreviewPending = (day) => {
    if (!day) return false;
    return bookingsForPreviewDate(previewDateStr(day)).some(b => b.status === "pending");
  };

  const isPreviewSelected = (day) => {
    if (!day) return false;
    return previewDateStr(day) === previewDate;
  };

  const prevPreviewMonth = () => {
    if (previewMonth === 0) {
      setPreviewYear(y => y - 1);
      setPreviewMonth(11);
    } else {
      setPreviewMonth(m => m - 1);
    }
  };

  const nextPreviewMonth = () => {
    if (previewMonth === 11) {
      setPreviewYear(y => y + 1);
      setPreviewMonth(0);
    } else {
      setPreviewMonth(m => m + 1);
    }
  };

  return (
    <div data-testid="calendar-admin">
      <div className="flex justify-between items-center mb-8">
        <h2 className="font-anton text-2xl text-white uppercase">Zarządzanie Kalendarzem</h2>
      </div>

      <div className="mb-10 grid 2xl:grid-cols-[420px_1fr] gap-6">
        <div className="border border-white/8 p-6" style={{ background: "#0A0A0A" }}>
          <div className="flex justify-between items-center mb-4">
            <button onClick={prevPreviewMonth} className="text-white/40 hover:text-white p-1">
              <ChevronLeft size={17} />
            </button>

            <h4 className="font-anton text-base text-white uppercase">
              {MONTHS_PL[previewMonth]} {previewYear}
            </h4>

            <button onClick={nextPreviewMonth} className="text-white/40 hover:text-white p-1">
              <ChevronRight size={17} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAYS_PL.map(d => (
              <div key={d} className="text-center text-[10px] text-white/20 uppercase py-1">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {previewDays.map((day, idx) => (
              <button
                key={idx}
                disabled={!day}
                onClick={() => day && setPreviewDate(previewDateStr(day))}
                className={`aspect-square relative flex items-center justify-center text-sm transition-all rounded-sm border
                  ${!day ? "invisible" : ""}
                  ${isPreviewSelected(day) ? "bg-violet-600 text-white border-violet-600 shadow-glow-violet" : ""}
                  ${!isPreviewSelected(day) && hasPreviewBooking(day) ? "text-white border-violet-600/30 bg-violet-600/10 hover:bg-violet-600/20" : ""}
                  ${!isPreviewSelected(day) && !hasPreviewBooking(day) ? "text-white/30 border-white/5 hover:border-white/15" : ""}
                `}
              >
                {day}

                {hasPreviewBooking(day) && (
                  <span
                    className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${
                      hasPreviewPending(day) ? "bg-yellow-400" : "bg-violet-400"
                    }`}
                  />
                )}
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-white/30">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 bg-violet-400 rounded-full inline-block" /> Wizyta
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 bg-yellow-400 rounded-full inline-block" /> Oczekuje
            </span>
          </div>
        </div>

        <div className="border border-white/8 p-6" style={{ background: "#0A0A0A" }}>
          <div className="flex justify-between items-start mb-5">
            <div>
              <h3 className="font-outfit text-sm font-semibold text-white">Wizyty dnia</h3>
              <p className="text-violet-400 text-xs mt-1">{previewDate}</p>
            </div>

            <div className="text-white/30 text-xs">
              {selectedPreviewBookings.length} wizyt
            </div>
          </div>

          {selectedPreviewBookings.length === 0 ? (
            <div className="text-white/25 text-sm py-10 text-center border border-white/5 border-dashed">
              Brak wizyt w tym dniu.
            </div>
          ) : (
            <div className="space-y-3">
              {selectedPreviewBookings.map(b => (
                <div key={b.id} className="border border-white/8 p-4" style={{ background: "#111111" }}>
                  <div className="flex items-center gap-2 mb-1">
                    <Clock size={13} className="text-violet-400" />
                    <span className="font-anton text-lg text-white">{b.time}</span>
                    <span className={`text-[10px] uppercase tracking-[0.12em] border px-2 py-0.5 ${STATUS_COLORS[b.status] || "text-white/50 border-white/15"}`}>
                      {STATUS_PL[b.status] || b.status}
                    </span>
                  </div>

                  <div className="text-white font-outfit font-medium">
                    {b.client_name}
                  </div>

                  <div className="text-white/45 text-xs mt-1">
                    {b.service_name || "Brak usługi"}
                    {b.row_label ? ` · ${b.row_label}` : ""}
                    {b.row_price ? ` · ${b.row_price}` : ""}
                  </div>

                  <div className="grid md:grid-cols-2 gap-2 mt-3 text-xs">
                    {b.client_email && (
                      <a href={`mailto:${b.client_email}`} className="text-violet-400 hover:text-white flex items-center gap-1.5">
                        <Mail size={12} /> {b.client_email}
                      </a>
                    )}

                    {b.client_phone && (
                      <a href={`tel:${b.client_phone}`} className="text-white/50 hover:text-white flex items-center gap-1.5">
                        <Phone size={12} /> {b.client_phone}
                      </a>
                    )}
                  </div>

                  {b.notes && (
                    <div className="mt-3 pt-3 border-t border-white/5">
                      <div className="text-white/25 text-[10px] uppercase tracking-[0.15em] mb-1">
                        Uwagi
                      </div>
                      <div className="text-white/50 text-xs max-h-24 overflow-y-auto whitespace-pre-wrap break-words pr-2">
                        {b.notes}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 1. Godziny pracy */}
      <div className="mb-10">
        <h3 className="font-outfit text-xs uppercase tracking-[0.2em] text-white/40 mb-4">Godziny pracy (tygodniowo)</h3>
        <div className="space-y-2">
          {workingHours.map(wh => (
            <div key={wh.day_of_week} className={`border p-4 transition-all ${wh.is_working ? "border-white/8" : "border-white/3 opacity-60"}`} style={{ background: "#0A0A0A" }}>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-3 w-36 flex-shrink-0">
                  <button data-testid={`toggle-day-${wh.day_of_week}`}
                    onClick={() => updateDay(wh.day_of_week, "is_working", !wh.is_working)}
                    className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 overflow-hidden ${wh.is_working ? "bg-violet-600" : "bg-white/10"}`}>
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${wh.is_working ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                  </button>
                  <span className="text-white text-sm font-outfit">{wh.day_name}</span>
                  {saving === wh.day_of_week && <div className="w-3 h-3 border border-violet-500 border-t-transparent rounded-full animate-spin" />}
                </div>
                {wh.is_working ? (
                  <>
                    <div className="flex items-center gap-2">
                      <Clock size={12} className="text-white/30" />
                      <label className="text-white/30 text-xs">Od</label>
                      <input type="time" value={wh.start_time} onChange={e => updateDay(wh.day_of_week, "start_time", e.target.value)}
                        className="bg-[#111111] border border-white/15 text-white text-sm px-2 py-1 outline-none focus:border-violet-600" style={{ colorScheme: "dark" }} />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-white/30 text-xs">Do</label>
                      <input type="time" value={wh.end_time} onChange={e => updateDay(wh.day_of_week, "end_time", e.target.value)}
                        className="bg-[#111111] border border-white/15 text-white text-sm px-2 py-1 outline-none focus:border-violet-600" style={{ colorScheme: "dark" }} />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-white/30 text-xs">Co</label>
                      <select value={wh.slot_duration} onChange={e => updateDay(wh.day_of_week, "slot_duration", Number(e.target.value))}
                        className="bg-[#111111] border border-white/15 text-white text-sm px-2 py-1 outline-none" style={{ colorScheme: "dark" }}>
                        {INTERVAL_OPTIONS.map(n => <option key={n} value={n} style={{ background: "#111111" }}>{n} min</option>)}
                      </select>
                    </div>
                  </>
                ) : (<span className="text-white/25 text-sm italic">Nieczynne</span>)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 2. Generuj (365 dni) */}
      <div className="mb-10 border border-violet-600/20 p-5" style={{ background: "rgba(139,0,255,0.04)" }}>
        <h3 className="font-outfit text-xs uppercase tracking-[0.2em] text-violet-400 mb-2">Zastosuj zmiany / Generuj terminy</h3>
        <p className="text-white/40 text-xs mb-4">
          Po zmianie godzin kliknij przycisk — wygeneruje terminy na <strong className="text-white/60">365 dni</strong> do przodu. Zarezerwowane wizyty NIE zostaną usunięte.
        </p>
        <div className="flex items-center gap-4">
          <button data-testid="regenerate-slots-btn" onClick={regenerate} disabled={regenerating} className="btn-primary text-xs disabled:opacity-50">
            {regenerating ? "Generowanie..." : "Generuj Terminy (365 dni)"} <RefreshCw size={13} />
          </button>
          {regenMsg && <span className="text-green-400 text-xs">{regenMsg}</span>}
        </div>
      </div>

      {/* 3. Dni wolne z zakresem */}
      <div className="mb-10">
        <h3 className="font-outfit text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Dni wolne / Zamknięte</h3>
        <p className="text-white/25 text-xs mb-4">Możesz podać jedną datę LUB zakres (np. urlop 8.08 - 18.08).</p>
        <form onSubmit={addClosed} className="flex flex-wrap gap-3 mb-6 items-end">
          <div>
            <label className="text-white/35 text-xs block mb-1">Od (data) *</label>
            <input type="date" value={newClosed.date} required onChange={e => setNewClosed({ ...newClosed, date: e.target.value })}
              className="bg-[#111111] border border-white/15 text-white text-sm px-3 py-2 outline-none focus:border-violet-600" style={{ colorScheme: "dark" }} />
          </div>
          <div>
            <label className="text-white/35 text-xs block mb-1">Do (zakres) — opcjonalnie</label>
            <input type="date" value={newClosed.date_end} min={newClosed.date}
              onChange={e => setNewClosed({ ...newClosed, date_end: e.target.value })}
              className="bg-[#111111] border border-white/15 text-white text-sm px-3 py-2 outline-none focus:border-violet-600" style={{ colorScheme: "dark" }} />
          </div>
          <div className="flex-1 min-w-40">
            <label className="text-white/35 text-xs block mb-1">Powód (opcjonalnie)</label>
            <input type="text" placeholder="np. Urlop" value={newClosed.reason}
              onChange={e => setNewClosed({ ...newClosed, reason: e.target.value })}
              className="bg-[#111111] border border-white/15 text-white text-sm px-3 py-2 outline-none focus:border-violet-600 w-full" style={{ colorScheme: "dark" }} />
          </div>
          <button type="submit" className="btn-primary text-xs flex-shrink-0"><Plus size={13} /> Dodaj</button>
        </form>
        <div className="space-y-2">
          {closedDates.length === 0 && <p className="text-white/25 text-sm">Brak dodanych dni wolnych.</p>}
          {closedDates.map(c => (
            <div key={c.id} className="flex justify-between items-center border border-white/5 px-4 py-3" style={{ background: "#0A0A0A" }}>
              <div>
                <span className="text-white font-outfit text-sm font-medium">{c.date}</span>
                {c.date_end && c.date_end !== c.date && <span className="text-violet-400 text-sm ml-2">→ {c.date_end}</span>}
                {c.reason && <span className="text-white/35 text-xs ml-3">{c.reason}</span>}
              </div>
              <button onClick={() => removeClosed(c.id)} className="text-red-400/50 hover:text-red-400 p-1"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      {/* 4. Wyjątkowe godziny */}
      <div className="mb-10">
        <h3 className="font-outfit text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Wyjątkowe godziny dla konkretnej daty</h3>
        <p className="text-white/25 text-xs mb-5">Inne godziny dla jednego dnia — zastępuje harmonogram tygodniowy.</p>
        <form onSubmit={addSpecial} className="border border-violet-600/15 p-5 mb-6" style={{ background: "#0A0A0A" }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-3">
            <div><label className="text-white/35 text-xs block mb-1">Data *</label>
              <input type="date" required value={newSpecial.date} onChange={e => setNewSpecial({ ...newSpecial, date: e.target.value })}
                className="bg-[#111111] border border-white/15 text-white text-sm px-3 py-2 outline-none w-full" style={{ colorScheme: "dark" }} /></div>
            <div><label className="text-white/35 text-xs block mb-1">Od *</label>
              <input type="time" required value={newSpecial.start_time} onChange={e => setNewSpecial({ ...newSpecial, start_time: e.target.value })}
                className="bg-[#111111] border border-white/15 text-white text-sm px-3 py-2 outline-none w-full" style={{ colorScheme: "dark" }} /></div>
            <div><label className="text-white/35 text-xs block mb-1">Do *</label>
              <input type="time" required value={newSpecial.end_time} onChange={e => setNewSpecial({ ...newSpecial, end_time: e.target.value })}
                className="bg-[#111111] border border-white/15 text-white text-sm px-3 py-2 outline-none w-full" style={{ colorScheme: "dark" }} /></div>
            <div><label className="text-white/35 text-xs block mb-1">Co (min)</label>
              <select value={newSpecial.slot_duration} onChange={e => setNewSpecial({ ...newSpecial, slot_duration: Number(e.target.value) })}
                className="bg-[#111111] border border-white/15 text-white text-sm px-2 py-2 outline-none w-full" style={{ colorScheme: "dark" }}>
                {INTERVAL_OPTIONS.map(n => <option key={n} value={n} style={{ background: "#111111" }}>{n} min</option>)}
              </select></div>
          </div>
          <div className="flex gap-3">
            <input type="text" placeholder="Powód (opcjonalnie)" value={newSpecial.reason}
              onChange={e => setNewSpecial({ ...newSpecial, reason: e.target.value })}
              className="bg-[#111111] border border-white/15 text-white text-sm px-3 py-2 outline-none flex-1" style={{ colorScheme: "dark" }} />
            <button type="submit" className="btn-primary text-xs flex-shrink-0"><Plus size={13} /> Dodaj</button>
          </div>
        </form>
        <div className="space-y-2">
          {specialDates.length === 0 && <p className="text-white/25 text-sm">Brak wyjątkowych godzin.</p>}
          {specialDates.map(s => (
            <div key={s.id} className="flex justify-between items-center border border-violet-600/15 px-4 py-3" style={{ background: "#0A0A0A" }}>
              <div>
                <span className="text-white font-outfit text-sm font-medium">{s.date}</span>
                <span className="text-violet-400 text-xs ml-3">{s.start_time}-{s.end_time}</span>
                <span className="text-white/30 text-xs ml-2">co {s.slot_duration} min</span>
                {s.reason && <span className="text-white/35 text-xs ml-3">· {s.reason}</span>}
              </div>
              <button onClick={() => removeSpecial(s.id)} className="text-red-400/50 hover:text-red-400 p-1"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      {/* 5. Zajęte terminy — przeniesienie z zeszytu */}
      <div>
        <h3 className="font-outfit text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Zajęte terminy (przeniesienie z zeszytu)</h3>
        <p className="text-white/25 text-xs mb-5">
          Wpisz zakres dat i godzin które są już zajęte. System zablokuje te sloty — klientki ich nie zobaczą.
        </p>
        <form onSubmit={addBlock} className="border border-yellow-500/20 p-5 mb-6" style={{ background: "rgba(234,179,8,0.03)" }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-3">
            <div><label className="text-white/35 text-xs block mb-1">Daty od *</label>
              <input type="date" required value={newBlock.date_from}
                onChange={e => setNewBlock({ ...newBlock, date_from: e.target.value, date_to: newBlock.date_to || e.target.value })}
                className="bg-[#111111] border border-white/15 text-white text-sm px-3 py-2 outline-none w-full" style={{ colorScheme: "dark" }} /></div>
            <div><label className="text-white/35 text-xs block mb-1">Daty do *</label>
              <input type="date" required value={newBlock.date_to} min={newBlock.date_from}
                onChange={e => setNewBlock({ ...newBlock, date_to: e.target.value })}
                className="bg-[#111111] border border-white/15 text-white text-sm px-3 py-2 outline-none w-full" style={{ colorScheme: "dark" }} /></div>
            <div><label className="text-white/35 text-xs block mb-1">Godziny od *</label>
              <input type="time" required value={newBlock.time_from}
                onChange={e => setNewBlock({ ...newBlock, time_from: e.target.value })}
                className="bg-[#111111] border border-white/15 text-white text-sm px-3 py-2 outline-none w-full" style={{ colorScheme: "dark" }} /></div>
            <div><label className="text-white/35 text-xs block mb-1">Godziny do *</label>
              <input type="time" required value={newBlock.time_to}
                onChange={e => setNewBlock({ ...newBlock, time_to: e.target.value })}
                className="bg-[#111111] border border-white/15 text-white text-sm px-3 py-2 outline-none w-full" style={{ colorScheme: "dark" }} /></div>
          </div>
          <div className="mb-3">
            <label className="text-white/35 text-xs block mb-1">Tylko wybrane dni tygodnia (opcjonalnie — brak zaznaczenia = wszystkie)</label>
            <div className="flex gap-2 flex-wrap">
              {DOW_LABELS.map((label, i) => (
                <button key={i} type="button"
                  onClick={() => { const cur = newBlock.days_of_week || []; setNewBlock({ ...newBlock, days_of_week: cur.includes(i) ? cur.filter(d => d !== i) : [...cur, i] }); }}
                  className={`text-xs px-3 py-1 border transition-colors ${(newBlock.days_of_week || []).includes(i) ? "border-yellow-500 bg-yellow-500/15 text-yellow-300" : "border-white/15 text-white/40 hover:border-white/30"}`}>
                  {label}
                </button>
              ))}
              {newBlock.days_of_week && newBlock.days_of_week.length > 0 && (
                <button type="button" onClick={() => setNewBlock({ ...newBlock, days_of_week: null })} className="text-xs text-white/25 hover:text-white px-2">wyczyść</button>
              )}
            </div>
          </div>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="text-white/35 text-xs block mb-1">Opis</label>
              <input type="text" value={newBlock.reason} onChange={e => setNewBlock({ ...newBlock, reason: e.target.value })}
                className="bg-[#111111] border border-white/15 text-white text-sm px-3 py-2 outline-none w-full" style={{ colorScheme: "dark" }} />
            </div>
            <button type="submit" disabled={blocking} className="btn-primary text-xs flex-shrink-0 disabled:opacity-50" style={{ borderColor: "rgb(234 179 8 / 0.5)" }}>
              {blocking ? "Blokuję..." : <><Plus size={13} /> Zablokuj</>}
            </button>
          </div>
          {blockMsg && <p className={`mt-3 text-xs ${blockMsg.startsWith("Zablok") ? "text-green-400" : "text-red-400"}`}>{blockMsg}</p>}
        </form>

        {blocked.length > 0 && (
          <div>
            <p className="text-white/30 text-xs mb-3">Zablokowane ({blockedTotal}) — kliknij kosz aby odblokować</p>
            <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
              {blocked.map(b => (
                <div key={b.id} className="flex justify-between items-center border border-yellow-500/10 px-3 py-2" style={{ background: "#0A0A0A" }}>
                  <div className="text-xs">
                    <span className="text-white/70">{b.date}</span>
                    <span className="text-yellow-400 ml-2">{b.time}</span>
                    <span className="text-white/30 ml-2">· {b.row_label || "-"}</span>
                  </div>
                  <button onClick={() => removeBlock(b.id)} className="text-red-400/50 hover:text-red-400 p-1 ml-2"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
            {blocked.length < blockedTotal && (
              <button onClick={() => { const ns = blockedSkip + BLOCKED_LIMIT; setBlockedSkip(ns); ax().get(`${API}/slots/blocked?skip=${ns}&limit=${BLOCKED_LIMIT}`).then(r => { const d = r.data; setBlocked(prev => [...prev, ...(d.items||d)]); setBlockedTotal(d.total||d.length); }); }}
                className="btn-secondary text-xs mt-3">
                Pokaż więcej <ChevronRight size={13} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
function ContactsTab() {
  const [items, setItems] = useState([]);
  const load = () => { ax().get(`${API}/contact`).then(r => setItems(r.data)).catch(console.error); };
  useEffect(() => { load(); }, []);
  const markRead = async id => { await ax().put(`${API}/contact/${id}`, { is_read: true }); load(); };
  return (
    <div data-testid="contacts-admin">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-anton text-2xl text-white uppercase">Wiadomości</h2>
        <button onClick={load} className="text-white/40 hover:text-white"><RefreshCw size={16} /></button>
      </div>
      <div className="space-y-4">
        {items.map(c => (
          <div key={c.id} className={`border p-6 ${c.is_read ? "border-white/5 opacity-60" : "border-violet-600/20"}`} style={{ background: "#0A0A0A" }}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <span className="font-outfit font-medium text-white">{c.name}</span>
                <span className="text-white/40 text-xs ml-2">{c.email}</span>
                {c.phone && <span className="text-white/30 text-xs ml-2">{c.phone}</span>}
              </div>
              <div className="flex items-center gap-3">
                {!c.is_read && (
                  <button data-testid={`mark-read-${c.id}`} onClick={() => markRead(c.id)}
                    className="text-xs text-violet-400 hover:text-white border border-violet-600/30 px-3 py-1">
                    Oznacz jako przeczytane
                  </button>
                )}
                {c.is_read && <span className="text-xs text-white/20">Przeczytane</span>}
              </div>
            </div>
            {c.subject && <div className="text-xs text-violet-400 uppercase tracking-[0.1em] mb-2">{c.subject}</div>}
            <p className="text-white/70 text-sm leading-relaxed">{c.message}</p>
            <div className="text-white/20 text-xs mt-3">{c.created_at ? new Date(c.created_at).toLocaleDateString("pl-PL") : ""}</div>
          </div>
        ))}
        {items.length === 0 && <p className="text-white/30 text-center py-12">Brak wiadomości.</p>}
      </div>
    </div>
  );
}

function DashboardTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => { ax().get(`${API}/admin/stats`).then(r => setStats(r.data)).catch(console.error); }, []);
  if (!stats) return <div className="text-white/40 py-8">Ładowanie...</div>;
  return (
    <div data-testid="dashboard-tab">
      <h2 className="font-anton text-2xl text-white uppercase mb-8">Przegląd</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Wszystkie wizyty" value={stats.bookings} sub={`${stats.bookings_pending} oczekuje`} />
        <StatCard label="Zakupy szkoleń" value={stats.purchases} sub={`${stats.purchases_paid} opłaconych`} color="green" />
        <StatCard label="Zdjęcia w galerii" value={stats.gallery} />
        <StatCard label="Nowe wiadomości" value={stats.contacts_unread} />
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-white/5 p-6" style={{ background: "#0A0A0A" }}>
          <h3 className="font-outfit text-xs uppercase tracking-[0.15em] text-white/40 mb-4">Szybkie akcje</h3>
          <div className="space-y-3">
            {[
              { label: "Zarządzaj wizytami", tab: "bookings" },
              { label: "Dodaj szkolenie", tab: "trainings" },
              { label: "Dodaj zdjęcie do galerii", tab: "gallery" },
              { label: "Sprawdź wiadomości", tab: "contacts" },
            ].map(a => (
              <a key={a.tab} href={`#${a.tab}`} className="flex items-center justify-between py-2 border-b border-white/5 text-white/60 hover:text-white text-sm transition-colors">
                {a.label} <Eye size={14} />
              </a>
            ))}
          </div>
        </div>
        <div className="border border-white/5 p-6" style={{ background: "#0A0A0A" }}>
          <h3 className="font-outfit text-xs uppercase tracking-[0.15em] text-white/40 mb-4">Status systemu</h3>
          <div className="space-y-3 text-sm">
            {[
              { label: "API Backend", status: "Aktywny" },
              { label: "Baza danych", status: "Połączona" },
              { label: "Galeria", status: `${stats.gallery} zdjęć` },
              { label: "Przelewy24", status: "Placeholder (wymaga konfiguracji)" },
            ].map(s => (
              <div key={s.label} className="flex justify-between items-center border-b border-white/5 py-2">
                <span className="text-white/60">{s.label}</span>
                <span className="text-green-400 text-xs">{s.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("dashboard");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const tabComponents = {
    dashboard: <DashboardTab />,
    bookings: <BookingsTab />,
    calendar: <CalendarTab />,
    trainings: <TrainingsTab />,
    uslugi: <CennikTab />,
    gallery: <GalleryTab />,
    media: <MediaLibraryTab />,
    partners: <PartnersTab />,
    contacts: <ContactsTab />,
    emails: <EmailTemplatesTab />,
  };

  return (
    <div data-testid="admin-panel" className="min-h-screen flex bg-brand-base">
      {/* Sidebar */}
      <aside className={`${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:relative z-40 w-64 min-h-screen border-r border-white/5 flex flex-col transition-transform duration-300`} style={{ background: "#050505" }}>
        {/* Logo */}
        <div className="p-6 border-b border-white/5">
          <div className="font-anton text-lg text-white">FRYZJER</div>
          <div className="font-anton text-xs tracking-[0.3em]" style={{ color: "#A855F7" }}>ARTYSTA · ADMIN</div>
        </div>
        {/* Nav */}
        <nav className="flex-1 p-4 space-y-1">
          {TABS.map(t => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => { setTab(t.id); setMobileOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm text-left transition-all ${tab === t.id ? "text-white bg-violet-600/10 border border-violet-600/20" : "text-white/40 hover:text-white hover:bg-white/5 border border-transparent"}`}>
              {t.icon} {t.label}
            </button>
          ))}
        </nav>
        {/* Logout */}
        <div className="p-4 border-t border-white/5">
          <div className="text-white/30 text-xs mb-3">{user?.email}</div>
          <button data-testid="admin-logout" onClick={handleLogout}
            className="w-full flex items-center gap-2 text-red-400/70 hover:text-red-400 text-sm py-2 transition-colors">
            <LogOut size={14} /> Wyloguj się
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        {/* Top bar */}
        <div className="border-b border-white/5 px-6 py-4 flex justify-between items-center sticky top-0 z-30 bg-brand-base/80 backdrop-blur-xl">
          <button data-testid="admin-menu-toggle" className="md:hidden text-white/60" onClick={() => setMobileOpen(!mobileOpen)}>
            ☰
          </button>
          <h1 className="font-outfit text-sm text-white/60 uppercase tracking-[0.2em]">
            {TABS.find(t => t.id === tab)?.label}
          </h1>
          <a href="/" target="_blank" className="text-white/30 hover:text-white text-xs uppercase tracking-[0.15em]">
            Strona ↗
          </a>
        </div>
        <div className="p-6 md:p-8">
          <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            {tabComponents[tab]}
          </motion.div>
        </div>
      </main>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setMobileOpen(false)} />
      )}
    </div>
  );
}
